/**
 * Pinia Data Store for Cirrus Reader Data
 * Contains a store for anything related to the reading experience including current page, renderer etc
 */

import { defineStore } from 'pinia'
import type { 
  ICirrusReaderCombinedTheme,
  ICirrusReaderFontSetAsset, 
  ICirrusReaderPublicationStylePalette,
} from '../shared/CirrusReaderModel';
import { RendererType } from '../../../../app/configuration/types';


interface CirrusReaderSettingsState {
  theme?: ICirrusReaderPublicationStylePalette,
  availableRenderers: RendererType[],
  activeRenderer: RendererType,
  availableThemes: ICirrusReaderCombinedTheme[],
  ignoreAspectRatio: boolean,
  font: string,
  fontSize: string,
  lineHeight: string,
  wordSpacing: string, 
  letterSpacing: string,
  availableFonts: ICirrusReaderFontSetAsset[]
}


export const useCirrusReaderSettingsStore = defineStore('cirrusReaderSettings', {
  state: (): CirrusReaderSettingsState => ({
    theme: undefined,
    availableRenderers: [ RendererType.enum.SpreadSwipeRenderer ],
    activeRenderer: RendererType.enum.SpreadSwipeRenderer,
    ignoreAspectRatio: false,
    font: 'components.publication_default',
    fontSize: '100%',
    lineHeight: '100%',
    wordSpacing: 'components.publication_default',
    letterSpacing: 'components.publication_default',
    availableThemes: [{
        name: 'Default',
        backgroundDark: '#ffffff',
        backgroundLight: '#ffffff',
        foregroundDark: '#303030',
        foregroundLight: '#3e3e3e',
        accent: '#863ca7',
        highlightColor: '#863ca7',

        isDark: false,
        //uiOutlineLight: 'rgba(255,255,255,0.5)',
        //uiOutlineDark: 'rgba(255,255,255,0.2)',
        uiBackgroundLight: '#ffffff',
        uiBackgroundDark: '#ebebeb',
        uiForegroundLight: '#333333',
        uiForegroundDark: '#636363',
    },
    {
        name: 'Dark',
        backgroundDark: '#303030',
        backgroundLight: '#3e3e3e',
        foregroundDark: '#dedede',
        foregroundLight: '#ffffff',
        accent: '#d48872',
        highlightColor: 'yellow',

        isDark: true,
        //uiOutlineLight: 'rgba(255,255,255,0.5)',
        //uiOutlineDark: 'rgba(255,255,255,0.2)',
        uiBackgroundLight: '#424242',
        uiBackgroundDark: '#858585',
        uiForegroundLight: '#dedede',
        uiForegroundDark: '#ffffff',
    },
    {
        name: 'ContrastDark',
        backgroundLight: '#000000',
        backgroundDark: '#000000',
        foregroundLight: '#ffffff',
        foregroundDark: '#ffffff',
        accent: '#ffde00',
        highlightColor: 'yellow',

        isDark: true,
        //uiOutlineLight: 'rgba(255, 255, 255, 1)',
        //uiOutlineDark: 'rgba(255, 255, 255, 0.6)',
        uiBackgroundLight: '#000000',
        uiBackgroundDark: '#000000',
        uiForegroundLight: '#ffffff',
        uiForegroundDark: '#ffffff',
    },
    {
        name: 'Paperlike',
        backgroundLight: '#ece8e2',
        backgroundDark: '#b3a196',
        foregroundDark: '#38251d',
        foregroundLight: '#ece8e2',
        accent: '#D88C00',
        highlightColor: 'yellow',

        isDark: false,
        //uiOutlineLight: '#7f5e4d',
        //uiOutlineDark: '#a7958c',
        uiBackgroundLight: '#ece8e2',
        uiBackgroundDark: '#b3a196',
        uiForegroundLight: '#38251d',
        uiForegroundDark: '#825542',
    }],
    availableFonts: []
  }),
  actions: {
    setAvailableRenderers(renderers: RendererType[]){
      this.availableRenderers = renderers;
      if (renderers[0]) this.setActiveRenderer(renderers[0]);
    },
    setActiveRenderer(rendererName: RendererType) {
      this.activeRenderer = rendererName;
    },
    setAvailableFonts(fonts: ICirrusReaderFontSetAsset[]){
      console.info("CirrusReaderSettingsStore - Set Fonts\n", fonts);
      this.availableFonts = fonts;
    },
    setIgnoreAspectRatio(ignoreAspectRatio: boolean){
      if (this.ignoreAspectRatio !== ignoreAspectRatio){
        this.ignoreAspectRatio = ignoreAspectRatio;
      }
    },
    setFont(font: string){
      if (this.font !== font){
        this.font = font;
      }
    },
    setFontSize(fontSize: string){
      if (this.fontSize !== fontSize){
        this.fontSize = fontSize;
      }
    },
    setLineHeight(lineHeight: string){
      if (this.lineHeight !== lineHeight){
        this.lineHeight = lineHeight;
      }
    },
    setWordSpacing(wordSpacing: string){
      if (this.wordSpacing !== wordSpacing){
        this.wordSpacing = wordSpacing;
      }
    },
    setLetterSpacing(letterSpacing: string){
      if (this.letterSpacing !== letterSpacing){
        this.letterSpacing = letterSpacing;
      }
    },
    setTheme(theme: ICirrusReaderPublicationStylePalette){
      console.info("CirrusReaderSettingsStore - Set Theme\n", theme);
      this.theme = theme;
    }
  }
});
